/**
 * /admin/stripe-links — Founder-only diagnostic.
 * Renders red/green status for every Stripe env var the code checks:
 *  - 13 family/plan payment_link_env keys
 *  - 4 infra keys (STRIPE_API_KEY / WEBHOOK / generic / split)
 * One-click copy of the missing env var names so you can paste them
 * straight into an email to support@emergent.sh.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Loader2, CheckCircle2, XCircle, Copy, ArrowLeft, ShieldCheck,
  AlertTriangle, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}`,
});

const StripeLinksAdmin = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const r = await api.get('/academy/admin/stripe-links-status', { headers: authHeaders() });
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Access denied');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const missing = useMemo(() => {
    if (!data) return [];
    const fams = (data.family_plan_links || []).filter((r) => !r.is_set).map((r) => r.env_key);
    const infra = (data.infra_keys || []).filter((r) => !r.is_set).map((r) => r.env_key);
    return Array.from(new Set([...fams, ...infra]));
  }, [data]);

  const copyMissing = async () => {
    if (!missing.length) { toast.success('Nothing missing — all keys are set'); return; }
    try {
      await navigator.clipboard.writeText(missing.join('\n'));
      toast.success(`${missing.length} missing env var name${missing.length === 1 ? '' : 's'} copied`);
    } catch { toast.error('Copy failed — please copy manually'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (err || !data) {
    return (
      <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center px-6">
        <div className="text-center max-w-md" data-testid="stripe-links-forbidden">
          <ShieldCheck className="w-10 h-10 mx-auto text-orange-500 mb-4" />
          <h1 className="text-2xl font-black uppercase tracking-tight mb-2">Founder Access Only</h1>
          <p className="text-sm text-zinc-400">{err || 'Sign in as brandon@prsvr.com to view Stripe link diagnostics.'}</p>
          <Link to="/academy/login" className="inline-block mt-6 bg-orange-500 hover:bg-orange-400 text-black px-5 py-2.5 text-[11px] font-black uppercase tracking-[0.2em]" data-testid="stripe-links-signin">Sign In</Link>
        </div>
      </div>
    );
  }

  const { summary, family_plan_links, infra_keys } = data;

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white pt-16 pb-24 px-4 md:px-6" data-testid="stripe-links-admin">
      <div className="container mx-auto max-w-5xl">
        <Link to="/admin" className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-500 hover:text-white flex items-center gap-1 mb-4" data-testid="back-to-admin">
          <ArrowLeft className="w-3 h-3" /> Admin Hub
        </Link>

        {/* HERO */}
        <div className="text-[10px] tracking-[0.5em] uppercase font-black text-orange-500">Diagnostic</div>
        <h1 className="text-3xl md:text-5xl font-black uppercase tracking-tight leading-[1.02] mt-2">
          Stripe Links <span className="text-orange-500">Status</span>
        </h1>
        <p className="text-zinc-300 text-sm md:text-base mt-3 max-w-2xl leading-relaxed">
          One-shot health check for every Stripe env var the platform expects. Green = set on this environment. Red = missing (parents will see the amber "Margaret will invoice you" fallback instead of a green Pay Now button).
        </p>

        {/* SUMMARY CARDS */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-6">
          <SummaryCard title="Family Plan Links" set={summary.family_plan_links.set} total={summary.family_plan_links.total} />
          <SummaryCard title="Infra Keys" set={summary.infra_keys.set} total={summary.infra_keys.total} />
          <div className="border-2 border-zinc-900 bg-zinc-950 p-4">
            <div className="text-[9px] uppercase tracking-widest font-black text-zinc-500 mb-1">Missing Total</div>
            <div className={`text-3xl font-black ${missing.length ? 'text-red-400' : 'text-emerald-400'}`} data-testid="missing-total">
              {missing.length}
            </div>
            <button onClick={copyMissing} disabled={!missing.length}
              className="mt-2 text-[10px] uppercase tracking-widest font-black text-orange-500 hover:text-orange-300 disabled:text-zinc-600 flex items-center gap-1"
              data-testid="copy-missing-btn">
              <Copy className="w-3 h-3" /> {missing.length ? 'Copy missing keys' : 'All set'}
            </button>
          </div>
        </div>

        {/* FAMILY PLAN LINKS */}
        <div className="mt-10">
          <SectionTitle>Family · Plan · Payment Links (13 unique env vars)</SectionTitle>
          <div className="border-2 border-zinc-900 bg-zinc-950 overflow-hidden">
            <div className="hidden md:grid grid-cols-[24px_1.2fr_1.4fr_1fr_0.6fr_1fr] gap-3 px-4 py-2 border-b-2 border-zinc-900 text-[9px] uppercase tracking-widest font-black text-zinc-500">
              <div></div>
              <div>Env Key</div>
              <div>Family · Student · Plan</div>
              <div>Rate</div>
              <div>Reused</div>
              <div>Preview URL</div>
            </div>
            {family_plan_links.map((row, i) => <PlanRow key={`${row.env_key}-${i}`} row={row} />)}
          </div>
        </div>

        {/* INFRA KEYS */}
        <div className="mt-10">
          <SectionTitle>Infra Keys</SectionTitle>
          <div className="border-2 border-zinc-900 bg-zinc-950 overflow-hidden">
            {infra_keys.map((row) => <InfraRow key={row.env_key} row={row} />)}
          </div>
        </div>

        {/* NEXT STEPS */}
        {missing.length > 0 && (
          <div className="mt-10 border-2 border-orange-500 bg-orange-500/5 p-5" data-testid="next-steps">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-[10px] uppercase tracking-widest font-black text-orange-500">Action Required</div>
                <div className="text-sm text-white mt-1 leading-relaxed">
                  {missing.length} env var{missing.length === 1 ? ' is' : 's are'} missing on this environment. To fix production, email <a href="mailto:support@emergent.sh" className="text-orange-400 hover:text-orange-300 underline">support@emergent.sh</a> with the missing env var names + your Stripe Payment Link URLs.
                </div>
                <button onClick={copyMissing}
                  className="mt-3 inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-black px-4 py-2 text-[10px] font-black uppercase tracking-[0.2em]"
                  data-testid="copy-missing-cta">
                  <Copy className="w-3 h-3" /> Copy Missing Keys
                </button>
                <a href="https://dashboard.stripe.com/payment-links" target="_blank" rel="noreferrer"
                  className="ml-2 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-zinc-400 hover:text-white"
                  data-testid="open-stripe">
                  <ExternalLink className="w-3 h-3" /> Open Stripe Payment Links
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const SummaryCard = ({ title, set, total }) => {
  const complete = set === total;
  return (
    <div className={`border-2 p-4 ${complete ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-red-500/40 bg-red-500/5'}`}>
      <div className="text-[9px] uppercase tracking-widest font-black text-zinc-400 mb-1">{title}</div>
      <div className="flex items-baseline gap-1.5">
        <div className={`text-3xl font-black ${complete ? 'text-emerald-400' : 'text-red-400'}`}>{set}</div>
        <div className="text-sm text-zinc-500 font-black">/ {total}</div>
      </div>
      <div className="text-[10px] uppercase tracking-widest font-black text-zinc-500 mt-1">{complete ? 'All Set' : `${total - set} Missing`}</div>
    </div>
  );
};

const StatusDot = ({ ok }) => (
  ok
    ? <CheckCircle2 className="w-4 h-4 text-emerald-400" />
    : <XCircle className="w-4 h-4 text-red-400" />
);

const PlanRow = ({ row }) => (
  <div className={`grid grid-cols-[24px_1fr] md:grid-cols-[24px_1.2fr_1.4fr_1fr_0.6fr_1fr] gap-3 px-4 py-3 border-b border-zinc-900 last:border-b-0 text-[12px] items-center ${row.is_set ? '' : 'bg-red-500/5'}`}
    data-testid={`plan-row-${row.env_key}`}>
    <StatusDot ok={row.is_set} />
    <div className="font-mono text-[11px] text-white break-all">{row.env_key}</div>
    <div className="hidden md:block text-zinc-400">
      <div className="text-white font-black text-[11px]">{row.student}</div>
      <div className="text-[10px] text-zinc-500 uppercase tracking-widest">{row.family} · {row.plan}</div>
    </div>
    <div className="hidden md:block font-black text-white">${row.rate?.toLocaleString?.() || row.rate}</div>
    <div className="hidden md:block text-[9px] uppercase tracking-widest font-black text-zinc-500">
      {row.shared_key ? 'Reused' : ''}
    </div>
    <div className="hidden md:block font-mono text-[10px] text-zinc-400 break-all">
      {row.masked || <span className="text-red-400">— not set —</span>}
    </div>
  </div>
);

const InfraRow = ({ row }) => (
  <div className={`flex items-center gap-3 px-4 py-3 border-b border-zinc-900 last:border-b-0 ${row.is_set ? '' : 'bg-red-500/5'}`}
    data-testid={`infra-row-${row.env_key}`}>
    <StatusDot ok={row.is_set} />
    <div className="flex-1 min-w-0">
      <div className="font-mono text-[11px] text-white break-all">{row.env_key}</div>
      <div className="text-[10px] text-zinc-500 mt-0.5">{row.purpose}</div>
    </div>
    <div className="font-mono text-[10px] text-zinc-400 break-all hidden sm:block max-w-[40%] text-right">
      {row.masked || <span className="text-red-400">— not set —</span>}
    </div>
  </div>
);

const SectionTitle = ({ children }) => (
  <h2 className="text-[10px] tracking-[0.5em] uppercase font-black text-white mb-3">{children}</h2>
);

export default StripeLinksAdmin;
